import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, ShieldCheck, Clock, Trash2 } from "lucide-react";
import { FilterCard, AuditTable, DetalheDialog } from "./auditoria/AuditoriaComponents";

function RetencaoCard() {
  const [cfg, setCfg] = useState(null);
  const [days, setDays] = useState(1825);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/audit-logs/retention")
    .then((r) => { setCfg(r.data); setDays(r.data.retention_days); })
    .catch((e) => logger.warn("Falha ao carregar retenção", e));
  useEffect(() => { load(); }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      const r = await api.put("/audit-logs/retention", { retention_days: Number(days) });
      setCfg(r.data);
      toast.success("Retenção atualizada");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    } finally { setSaving(false); }
  };

  const purge = async () => {
    if (!window.confirm(`Executar purge? Serão removidos ${cfg?.elegiveis_para_purge || 0} registros antigos.`)) return;
    try {
      const r = await api.post("/audit-logs/purge");
      toast.success(`Removidos: ${r.data.removidos}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  if (!cfg) return null;
  return (
    <Card className="border-amber-200">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-5 h-5" />Retenção e Purge
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-600">
          Padrão TCU: 5 anos (1825 dias). Use 0 para retenção infinita. O purge automático roda diariamente às 04:00 UTC.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Retenção (dias)</Label>
            <Input
              type="number" min="0" max="10950"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="input-retention-days"
            />
          </div>
          <div className="text-xs text-slate-600">
            <div><strong>{cfg.total_registros.toLocaleString("pt-BR")}</strong> registros totais</div>
            <div className="text-amber-700"><strong>{cfg.elegiveis_para_purge.toLocaleString("pt-BR")}</strong> eligíveis para purge</div>
          </div>
          <Button onClick={salvar} disabled={saving} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-salvar-retencao">
            {saving ? "Salvando..." : "Salvar retenção"}
          </Button>
          <Button
            onClick={purge}
            disabled={!cfg.elegiveis_para_purge}
            variant="outline"
            className="text-red-600 border-red-300"
            data-testid="btn-purge-manual"
          >
            <Trash2 className="w-4 h-4 mr-2" />Purge agora
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const PAGE_SIZE = 50;
const emptyFilters = { action: "", status: "", date_from: "", date_to: "" };

export default function AuditoriaPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [labels, setLabels] = useState({});
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(emptyFilters);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
      });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api.get(`/audit-logs?${params.toString()}`);
      setItems(r.data.items);
      setTotal(r.data.total);
      setLabels(r.data.action_labels || {});
    } catch (e) {
      logger.error("Falha ao carregar auditoria", e);
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/audit-logs/actions")
      .then((r) => setActions(r.data.actions || []))
      .catch((e) => logger.warn("Falha ao carregar ações de auditoria", e));
  }, []);

  const setFilter = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v })); };
  const clearFilters = () => { setPage(0); setFilters(emptyFilters); };

  const exportCsv = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
    window.location.href = `${API}/audit-logs/export?${params.toString()}`;
  };

  const totalPages = useMemo(() => Math.ceil(total / PAGE_SIZE) || 1, [total]);

  return (
    <div className="space-y-6" data-testid="auditoria-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
            <ShieldCheck className="w-8 h-8" />Auditoria do Sistema
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Log imutável de ações sensíveis · <strong>{total.toLocaleString("pt-BR")}</strong> registros no filtro atual
          </p>
        </div>
        <Button onClick={exportCsv} variant="outline" className="border-[#083CA6] text-[#083CA6]" data-testid="btn-export-audit">
          <FileDown className="w-4 h-4 mr-2" />Exportar CSV
        </Button>
      </div>

      <RetencaoCard />

      <FilterCard
        filters={filters}
        actions={actions}
        labels={labels}
        onFilterChange={setFilter}
        onClear={clearFilters}
      />

      <AuditTable
        items={items}
        loading={loading}
        labels={labels}
        page={page}
        totalPages={totalPages}
        total={total}
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
        onOpenDetail={setDetail}
      />

      <DetalheDialog
        detail={detail}
        labels={labels}
        onClose={() => setDetail(null)}
      />
    </div>
  );
}
